import React, { useMemo, useState, useEffect } from 'react';
import { Download, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { logout } from '../utils/auth';
import { SESSION_KEY } from '../helper/common';
import '../App.css';

const config = {
    apiUrl: process.env.REACT_APP_API_URL_User,
};

const NOCOTP = () => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [error, setError] = useState('');
    const [balanceLoading, setBalanceLoading] = useState(true);
    const [outstandingAmt, setOutstandingAmt] = useState(null);
    const [balanceError, setBalanceError] = useState('');
    const navigate = useNavigate();

    const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');

    const decodeTokenClaims = (jwtToken) => {
        try {
            const payload = jwtToken?.split('.')?.[1];
            if (!payload) return {};
            const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
            const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
            const decoded = atob(padded);
            return JSON.parse(decoded);
        } catch {
            return {};
        }
    };

    const claims = useMemo(() => decodeTokenClaims(token), [token]);

    const cnicRaw = claims?.cnic || claims?.CNIC || claims?.idNumber || claims?.ID_NUMBER || localStorage.getItem('cnic') || '';
    const mobileRaw = claims?.mobileNo || claims?.mobile || claims?.MobileNo || localStorage.getItem('mobile') || '';
    const customerId =
        claims?.custId ||
        claims?.customerId ||
        claims?.CustomerId ||
        localStorage.getItem('custId') ||
        claims?.cardNo ||
        '';
    const cnic = cnicRaw ? String(cnicRaw).replace(/[^0-9]/g, '') : '';
    const phone = mobileRaw ? String(mobileRaw).replace(/[^0-9]/g, '') : '';
    const normalizedCustomerId = customerId ? String(customerId).trim() : '';
    const cardNoRaw = claims?.cardNo || claims?.CardNo || claims?.card_no || localStorage.getItem('cardNumber') || '';
    const cardNo = cardNoRaw ? String(cardNoRaw).trim() : '';

    useEffect(() => {
        if (!cardNo) {
            setBalanceLoading(false);
            setBalanceError('Card number not found. Cannot verify outstanding balance.');
            return;
        }
        const fetchOutstanding = async () => {
            try {
                setBalanceLoading(true);
                const token = localStorage.getItem(SESSION_KEY);
                const response = await axios.get(`${config.apiUrl}/api/cards/settings`, {
                    params: { cardNumber: cardNo },
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                });
                const xmlText = response.data?.value;
                if (!xmlText) throw new Error('Empty response from card settings.');
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
                const rawAmt = xmlDoc.getElementsByTagName('OutstandingAmt')[0]?.textContent;
                const parsed = parseFloat(rawAmt);
                setOutstandingAmt(isNaN(parsed) ? 0 : parsed);
            } catch (err) {
                const msg = err.response?.data?.message || err.message || 'Failed to retrieve outstanding balance.';
                setBalanceError(msg);
                toast.error(msg);
            } finally {
                setBalanceLoading(false);
            }
        };
        fetchOutstanding();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardNo]);

    const handleLogout = () => {
        logout();
        navigate('/', { replace: true });
    };

    const handleDownload = async () => {
        setError('');
        if (!token) {
            setError('No authentication token found. Please login again.');
            return;
        }

        if (!cnic || !normalizedCustomerId) {
            setError('Missing CNIC or Customer ID. Please login again.');
            return;
        }

        try {
            setIsDownloading(true);
            const token = localStorage.getItem(SESSION_KEY);

            const response = await axios.get(`${config.apiUrl}/api/cards/closure-letter`, {
                params: { cnic, customerId: normalizedCustomerId, phone },
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                responseType: 'blob',
            });

            const contentType = response.headers?.['content-type'] || '';
            if (!contentType.includes('application/pdf')) {
                throw new Error('Closure letter service did not return a PDF');
            }

            const fileUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = fileUrl;
            link.download = `ClosureLetter_${normalizedCustomerId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(fileUrl);
            toast.success('Closure letter downloaded successfully.');
        } catch (err) {
            let msg = 'Failed to download closure letter';
            if (err.response?.data instanceof Blob) {
                try {
                    const text = await err.response.data.text();
                    if (text) msg = text;
                } catch {
                }
            } else if (err.response?.data?.message) {
                msg = err.response.data.message;
            } else if (err.message) {
                msg = err.message;
            }
            console.error('Closure letter download error:', msg);
            setError(msg);
            toast.error(msg);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="min-h-[100vh] w-full flex items-center justify-center bg-slate-50 px-4">
            <div className="w-full max-w-xl bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={handleLogout}
                        className="text-sm text-slate-600 hover:text-slate-800 underline"
                    >
                        Logout
                    </button>
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Card Closure Letter</h2>
                <p className="text-slate-600 mt-2">
                    Click below to download your closure letter PDF.
                </p>

                <div className="mt-6 space-y-2 text-sm text-slate-600">
                    <p><strong>CNIC:</strong> {cnic || 'N/A'}</p>
                    <p><strong>Card No:</strong> {cardNo ? `•••• •••• •••• ${cardNo.slice(-4)}` : 'N/A'}</p>
                </div>

                {/* Outstanding balance check */}
                {balanceLoading ? (
                    <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
                        <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
                        Checking outstanding balance…
                    </div>
                ) : balanceError ? (
                    <div className="mt-6 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>{balanceError}</span>
                    </div>
                ) : outstandingAmt > 0 ? (
                    <div className="mt-6 flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg p-4 text-sm text-amber-800">
                        <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-amber-600" />
                        <div>
                            <p className="font-semibold">Outstanding Balance Detected</p>
                            <p className="mt-1">
                                Your card has an outstanding balance of{' '}
                                <strong>PKR {outstandingAmt.toLocaleString('en-PK', { minimumFractionDigits: 2 })}</strong>.
                                Please clear your balance before downloading the closure letter.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="mt-6 flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
                        <span className="font-medium">✓ No outstanding balance. You may download the closure letter.</span>
                    </div>
                )}

                {error && <p className="text-red-600 mt-4">{error}</p>}

                <button
                    type="button"
                    onClick={handleDownload}
                    disabled={isDownloading || balanceLoading || !!balanceError || outstandingAmt > 0}
                    className="mt-6 inline-flex items-center gap-2 bg-green-600 text-white px-5 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Download className="h-4 w-4" />
                    {isDownloading ? 'Downloading...' : 'Download PDF'}
                </button>
            </div>
        </div>
    );
};

export default NOCOTP;